import { useMemo, useState } from 'react';
import { Box, Chip, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import CardActionButton from 'src/components/CardActionButton';
import Iconify from 'src/components/Iconify';
import WarningIcon from 'src/components/WarningIcon';
import { pagesText } from 'src/constants/pagesText';
import { formatDate } from 'src/utils/formatTime';
import { handleZipFileDownload, policyArtifactZipName } from 'src/utils/download.util';
import { WDAC_POLICY_PURPOSE } from 'src/utils/enums/wdacPolicyPurpose';
import {
  useDownloadUnmatchedFilesCatalog,
  useGenerateUnmatchedFilesCatalog,
  useUnmatchedFilesPolicy,
} from 'src/hooks/wdac';
import GenerateSupplementalModal from 'src/features/basePolicy/GenerateSupplementalModal';

const MAX_VISIBLE_BASE_CHIPS = 4;

const GENERATING_MESSAGE =
  'This catalog is still being generated. Try the download again in a moment.';

const BASE_CHIP_SX = {
  height: 18,
  maxWidth: 140,
  color: '#D1D5DB',
  backgroundColor: 'rgba(255, 255, 255, 0.08)',
  border: '1px solid rgba(255, 255, 255, 0.14)',
  fontSize: '0.6rem',
  '& .MuiChip-label': { px: 0.75 },
};

const CardTitle = ({ unrepresentedFileCount }) => (
  <Stack direction="row" alignItems="center" gap={0.75}>
    <Typography sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '0.82rem', lineHeight: 1.3 }}>
      Security Catalogs
    </Typography>
    {unrepresentedFileCount > 0 ? (
      <WarningIcon
        icon="mdi:shield-alert-outline"
        width={16}
        height={16}
        message={`${unrepresentedFileCount.toLocaleString()} updated ${
          unrepresentedFileCount === 1 ? 'file' : 'files'
        } not represented in a security catalog yet`}
      />
    ) : null}
  </Stack>
);

const UnmatchedFilesCatalogCard = ({ sx, modalOpen: controlledOpen, onModalOpenChange }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [internalOpen, setInternalOpen] = useState(false);
  const modalOpen = controlledOpen ?? internalOpen;
  const setModalOpen = onModalOpenChange ?? setInternalOpen;
  const [downloading, setDownloading] = useState(false);

  const { data: policyState, isLoading } = useUnmatchedFilesPolicy();
  const { mutate: generateCatalog, isLoading: generating } = useGenerateUnmatchedFilesCatalog();
  const { mutateAsync: downloadCatalog } = useDownloadUnmatchedFilesCatalog();

  const activePolicies = useMemo(
    () => (policyState?.policies || []).filter(policy => policy.isActive),
    [policyState],
  );
  const hasActiveCatalog = activePolicies.length > 0;
  const trustedFileCount = policyState?.trustedFileCount ?? 0;
  const canCreate = trustedFileCount > 0;

  const visibleBasePolicies = activePolicies.slice(0, MAX_VISIBLE_BASE_CHIPS);
  const hiddenBaseCount = activePolicies.length - visibleBasePolicies.length;

  const onDownload = async () => {
    setDownloading(true);
    try {
      const blob = await downloadCatalog();
      handleZipFileDownload(
        blob,
        policyArtifactZipName(WDAC_POLICY_PURPOSE.UnmatchedFiles, policyState?.currentVersion),
      );
    } catch {
      // The download fails until the generation job has written the catalog, which is a wait
      // rather than an error. The API client drops the status, so every failure reads as that wait.
      enqueueSnackbar(GENERATING_MESSAGE, { variant: 'info', autoHideDuration: 4000 });
    } finally {
      setDownloading(false);
    }
  };

  const onSave = basePolicyIds =>
    generateCatalog(
      { basePolicyIds },
      {
        onSuccess: () => {
          setModalOpen(false);
          enqueueSnackbar('Catalog generation started', {
            variant: 'success',
            autoHideDuration: 3000,
          });
        },
        onError: () =>
          enqueueSnackbar(pagesText.somethingWentWrong, {
            variant: 'error',
            autoHideDuration: 3000,
          }),
      },
    );

  const createButton = (
    <CardActionButton
      disabled={!canCreate}
      onClick={() => setModalOpen(true)}
      startIcon={<Iconify icon="mdi:plus" width={15} height={15} />}>
      Create catalog
    </CardActionButton>
  );

  return (
    <Stack
      sx={{
        backgroundColor: '#454545',
        border: '1px solid #5B5B5B',
        borderRadius: '8px',
        p: 1.5,
        ...sx,
      }}
      gap={1}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
        <CardTitle unrepresentedFileCount={policyState?.unrepresentedFileCount ?? 0} />
        {!isLoading ? (
          hasActiveCatalog ? (
            <Stack direction="row" gap={1} flexShrink={0}>
              <CardActionButton
                disabled={downloading}
                onClick={onDownload}
                startIcon={<Iconify icon="mdi:download-outline" width={15} height={15} />}>
                {downloading ? 'Preparing…' : 'Download'}
              </CardActionButton>
              <CardActionButton
                disabled={generating}
                onClick={() => setModalOpen(true)}
                startIcon={<Iconify icon="mdi:refresh" width={15} height={15} />}>
                Update
              </CardActionButton>
            </Stack>
          ) : (
            createButton
          )
        ) : null}
      </Stack>

      {isLoading ? (
        <Stack gap={0.75} flex={1}>
          <Skeleton variant="rounded" height={24} width={200} sx={{ bgcolor: '#4B4B4B' }} />
          <Skeleton variant="rounded" height={20} sx={{ bgcolor: '#4B4B4B' }} />
        </Stack>
      ) : !hasActiveCatalog ? (
        <Stack direction="row" alignItems="center" gap={1} flex={1}>
          <Iconify icon="mdi:certificate-outline" width={24} height={24} color="#87C8E0" />
          <Typography sx={{ color: '#9CA3AF', fontSize: '0.78rem' }}>
            {trustedFileCount > 0
              ? `No catalog yet — cover all ${trustedFileCount.toLocaleString()} trusted files across your base policies.`
              : 'Trust some unmatched files first, then generate a security catalog.'}
          </Typography>
        </Stack>
      ) : (
        <Stack
          gap={0.75}
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.14)',
            border: '1px solid #5B5B5B',
            borderRadius: '8px',
            px: 1.25,
            py: 0.9,
          }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Chip
              size="small"
              label={policyState?.currentVersion || '—'}
              sx={{
                height: 20,
                color: '#7DFF92',
                backgroundColor: 'rgba(125, 255, 146, 0.08)',
                border: '1px solid rgba(125, 255, 146, 0.4)',
                fontWeight: 700,
                fontSize: '0.7rem',
              }}
            />
            <Typography sx={{ color: '#9CA3AF', fontSize: '0.72rem' }}>
              {policyState?.lastGeneratedAt
                ? `Generated ${formatDate(policyState.lastGeneratedAt)}`
                : 'Not generated yet'}
            </Typography>
          </Stack>
          <Stack direction="row" flexWrap="wrap" gap={0.5} alignItems="center">
            {visibleBasePolicies.map(policy => (
              <Chip
                key={policy.id}
                size="small"
                label={policy.basePolicyName || policy.basePolicyId || policy.policyName}
                sx={BASE_CHIP_SX}
              />
            ))}
            {hiddenBaseCount > 0 ? (
              <Tooltip
                title={activePolicies
                  .slice(MAX_VISIBLE_BASE_CHIPS)
                  .map(policy => policy.basePolicyName || policy.basePolicyId || policy.policyName)
                  .join(', ')}
                arrow
                componentsProps={{ tooltip: { sx: { bgcolor: '#3F3F3F', maxWidth: 300 } } }}>
                <Box component="span" display="inline-flex">
                  <Chip size="small" label={`+${hiddenBaseCount}`} sx={BASE_CHIP_SX} />
                </Box>
              </Tooltip>
            ) : null}
          </Stack>
        </Stack>
      )}

      <GenerateSupplementalModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={hasActiveCatalog ? 'update' : 'create'}
        policyState={policyState}
        onSave={onSave}
        loading={generating}
        icon="mdi:certificate-outline"
        createTitle="Create Trusted Files Catalog"
        updateTitle="Update Trusted Files Catalog"
        createSubmitLabel="Generate catalog"
        updateSubmitLabel="Update catalog"
        returnTarget="catalog"
        itemCount={trustedFileCount}
        description={`Generate a security catalog for the ${trustedFileCount.toLocaleString()} trusted file${
          trustedFileCount === 1 ? '' : 's'
        } for each selected base policy.`}
        requirementNote="Selected base policies must contain a catalog signing rule. Verify that each policy enables catalogs before generating the catalog."
      />
    </Stack>
  );
};

export default UnmatchedFilesCatalogCard;
